import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Eye, ArrowRightLeft, ShieldCheck } from "lucide-react";

const ROLE_LABELS = { super_admin: "Owner", admin: "Admin", telecaller: "Telecaller", accountant: "Accountant", team_leader: "Field Staff" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "team_leader", department: "", salary: "", joining_date: "", login_id: "" });
  const [permOpen, setPermOpen] = useState(false);
  const [permData, setPermData] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [customRoles, setCustomRoles] = useState([]);
  const [reassignFor, setReassignFor] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const leaders = users.filter((u) => u.role === "team_leader" && u.active);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
    if (me.role === "super_admin") api.get("/roles").then((r) => setCustomRoles(r.data.custom_roles)).catch(() => {});
  }, [me.role]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, salary: parseFloat(form.salary) || 0, joining_date: form.joining_date || null, login_id: form.login_id || null });
      toast.success("User created — they must change password on first login"); setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "team_leader", department: "", salary: "", joining_date: "", login_id: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openPermissions = async () => {
    try {
      const r = await api.get("/roles");
      const cfg = await api.get("/workspace/config").catch(() => null);
      const overrides = cfg?.data?.config?.permissions || {};
      setPermData({ defaults: r.data.default_roles, overrides });
      setPermOpen(true);
    } catch { toast.error("Could not load roles"); }
  };

  const savePermissions = async () => {
    try {
      await api.patch("/workspace/config", { section: "permissions", value: permData.overrides });
      toast.success("Permission matrix saved for this workspace");
      setPermOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const togglePerm = (role, mod, act) => {
    const cur = permData.overrides[role] || (permData.defaults.find((d) => d.name === role)?.permissions) || {};
    const acts = new Set(cur[mod] || []);
    acts.has(act) ? acts.delete(act) : acts.add(act);
    setPermData({ ...permData, overrides: { ...permData.overrides, [role]: { ...cur, [mod]: [...acts] } } });
  };

  const toggle = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? "User deactivated" : "User activated");
    load();
  };

  const bulkDeactivate = async () => {
    for (const id of selected) await api.patch(`/users/${id}`, { active: false });
    toast.success(`${selected.size} users deactivated`);
    setSelected(new Set()); load();
  };

  const impersonate = async (u) => {
    try {
      await api.post(`/users/${u.id}/impersonate`);
      sessionStorage.setItem("cm_impersonating", u.name);
      window.location.href = u.role === "team_leader" ? "/app" : "/admin";
    } catch (err) { toast.error(err.response?.data?.detail || "Only the Owner can impersonate"); }
  };

  const doReassign = async () => {
    try {
      const r = await api.post(`/users/${reassignFor.id}/reassign`, { to_user_id: reassignTo });
      toast.success(`${r.data.moved} jobs moved to ${r.data.to}`);
      setReassignFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => users.filter((u) =>
    (roleFilter === "all" || u.role === roleFilter) &&
    (!search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.includes(search.toLowerCase()))),
    [users, roleFilter, search]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Team Management</h1>
          <p className="text-sm text-muted-foreground">Every user, every role — search, impersonate, reassign, deactivate</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="destructive" data-testid="bulk-deactivate-button" onClick={bulkDeactivate}>Deactivate {selected.size}</Button>
          )}
          <Button variant="outline" data-testid="permissions-button" onClick={openPermissions}><ShieldCheck size={15} className="mr-2" /> Permissions</Button>
          <Button data-testid="add-user-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary-hover"><Plus size={16} className="mr-2" /> Add Staff</Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Input data-testid="user-search-input" placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64 h-9" />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-44 h-9" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3 w-8"></th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Login ID</th>
              <th className="px-4 py-3">Role</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Last Login</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t hover:bg-accent/40 transition-colors">
                <td className="px-4 py-3"><input type="checkbox" data-testid={`user-select-${u.id}`} checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} disabled={u.id === me.id} /></td>
                <td className="px-4 py-3 font-medium">{u.name}{u.custom_role && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary border border-primary">{u.custom_role}</span>}</td>
                <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{u.login_id || u.email}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full border bg-primary text-primary border-primary">{ROLE_LABELS[u.role]}</span></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{u.department || "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{u.login_history?.length ? new Date(u.login_history[u.login_history.length - 1]).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Never"}</td>
                <td className="px-4 py-3">{u.active
                  ? <span className="text-xs px-2 py-1 rounded-full border bg-success-tint text-success border-success/30">Active</span>
                  : <span className="text-xs px-2 py-1 rounded-full border bg-muted text-muted-foreground border-border">Inactive</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 justify-end">
                    {me.role === "super_admin" && u.id !== me.id && (
                      <Button size="sm" variant="outline" data-testid={`impersonate-${u.id}`} onClick={() => impersonate(u)} className="h-8" title="View app as this user"><Eye size={13} /></Button>
                    )}
                    {u.role === "team_leader" && (
                      <Button size="sm" variant="outline" data-testid={`reassign-${u.id}`} onClick={() => { setReassignFor(u); setReassignTo(""); }} className="h-8" title="Reassign their jobs"><ArrowRightLeft size={13} /></Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`user-toggle-${u.id}`} onClick={() => toggle(u)} className="h-8" disabled={u.id === me.id}>{u.active ? "Deactivate" : "Activate"}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">New User</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <Input data-testid="user-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input data-testid="user-email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="user-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Input data-testid="user-department-input" placeholder="Department (e.g. Sales)" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Monthly salary (₹)</label>
                <Input data-testid="user-salary-input" type="number" min="0" placeholder="0" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Joining date</label>
                <Input data-testid="user-joining-input" type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="user-loginid-input" placeholder="Login ID (blank = auto)" value={form.login_id} onChange={(e) => setForm({ ...form, login_id: e.target.value })} />
              <Input data-testid="user-password-input" type="text" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
            </div>
            <div className="text-[11px] text-muted-foreground">Staff log in with Company Code + Login ID + Password. They must set a new password on first login.</div>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem><SelectItem value="telecaller">Telecaller</SelectItem>
                <SelectItem value="accountant">Accountant</SelectItem><SelectItem value="team_leader">Field Staff</SelectItem>
                <SelectItem value="super_admin">Owner</SelectItem>
              </SelectContent>
            </Select>
            {customRoles.length > 0 && (
              <Select value={form.custom_role || ""} onValueChange={(v) => setForm({ ...form, custom_role: v })}>
                <SelectTrigger data-testid="user-custom-role-select"><SelectValue placeholder="Extra custom role (optional)" /></SelectTrigger>
                <SelectContent>{customRoles.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button data-testid="user-save-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Create User</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="permissions-dialog">
          <DialogHeader><DialogTitle className="font-heading">Permission Matrix — this workspace</DialogTitle></DialogHeader>
          {permData && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Toggle actions per role per module. Changes apply to everyone in this workspace on next login.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="permissions-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                      <th className="py-2 pr-2">Role</th><th className="py-2 pr-2">Module</th>
                      {["view", "create", "edit", "delete", "export"].map((a) => <th key={a} className="py-2 px-1 text-center">{a}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {permData.defaults.filter((r) => r.name !== "super_admin").map((r) => {
                      const effective = permData.overrides[r.name] || r.permissions;
                      const mods = Object.keys({ ...r.permissions, ...effective });
                      return mods.map((mod, mi) => (
                        <tr key={`${r.name}-${mod}`} className="border-b last:border-0">
                          {mi === 0 && <td className="py-1.5 pr-2 font-semibold align-top" rowSpan={mods.length}>{ROLE_LABELS[r.name] || r.name}</td>}
                          <td className="py-1.5 pr-2 text-muted-foreground">{mod}</td>
                          {["view", "create", "edit", "delete", "export"].map((a) => (
                            <td key={a} className="py-1.5 px-1 text-center">
                              <input type="checkbox" data-testid={`perm-${r.name}-${mod}-${a}`} checked={(effective[mod] || []).includes(a)} onChange={() => togglePerm(r.name, mod, a)} className="w-3.5 h-3.5 accent-indigo-600" />
                            </td>
                          ))}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
              <Button data-testid="permissions-save-button" onClick={savePermissions} className="w-full bg-primary hover:bg-primary-hover">Save Matrix</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignFor} onOpenChange={() => setReassignFor(null)}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reassign {reassignFor?.name}'s active jobs</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger data-testid="reassign-target-select"><SelectValue placeholder="Move jobs to…" /></SelectTrigger>
              <SelectContent>{leaders.filter((l) => l.id !== reassignFor?.id).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="reassign-confirm-button" onClick={doReassign} disabled={!reassignTo} className="w-full bg-primary hover:bg-primary-hover">Move All Active Jobs</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
